/*
 * @Description:
 */
// Parse the time to string
import dayjs from 'dayjs'

export const parseTime = (
  time?: object | string | number | null,
  cFormat?: string
): string | null => {
  if (time === undefined || !time) {
    return null
  }
  const format = cFormat || '{y}-{m}-{d} {h}:{i}:{s}'
  let date: Date
  if (typeof time === 'object') {
    date = time as Date
  } else {
    if (typeof time === 'string') {
      if (/^[0-9]+$/.test(time)) {
        // support "1548221490638"
        time = parseInt(time)
      } else {
        // support safari
        // https://stackoverflow.com/questions/4310953/invalid-date-in-safari
        time = time.replace(new RegExp(/-/gm), '/')
      }
    }
    if (typeof time === 'number' && time.toString().length === 10) {
      time = time * 1000
    }
    date = new Date(time)
  }
  const formatObj: { [key: string]: number } = {
    y: date.getFullYear(),
    m: date.getMonth() + 1,
    d: date.getDate(),
    h: date.getHours(),
    i: date.getMinutes(),
    s: date.getSeconds(),
    a: date.getDay()
  }
  const timeStr = format.replace(/{([ymdhisa])+}/g, (result, key) => {
    const value = formatObj[key]
    // Note: getDay() returns 0 on Sunday
    if (key === 'a') {
      return ['日', '一', '二', '三', '四', '五', '六'][value]
    }
    return value.toString().padStart(2, '0')
  })
  return timeStr
}

export function formatDate(date: Date | null, format?: string) {
  format = format || 'yyyy-MM-dd hh:mm:ss'
  if (date == null) {
    return ''
  }
  const _date = new Date(date)
  if (/(y+)/.test(format)) {
    format = format.replace(RegExp.$1, (_date.getFullYear() + '').substr(4 - RegExp.$1.length))
  }
  const o = {
    'M+': _date.getMonth() + 1,
    'd+': _date.getDate(),
    'h+': _date.getHours(),
    'm+': _date.getMinutes(),
    's+': _date.getSeconds()
  }
  for (const k in o) {
    if (new RegExp(`(${k})`).test(format)) {
      const str = o[k] + ''
      format = format.replace(RegExp.$1, (RegExp.$1.length === 1) ? str : ('00' + str).substr(str.length))
    }
  }
  return format
}

// Format and filter json data using filterKeys array
export const formatJson = (filterKeys: any, jsonData: any) =>
  jsonData.map((data: any) => filterKeys.map((key: string) => {
    if (key === 'timestamp') {
      return parseTime(data[key])
    } else {
      return data[key]
    }
  }))

// Check if an element has a class
export const hasClass = (ele: HTMLElement, className: string) => {
  return !!ele.className.match(new RegExp('(\\s|^)' + className + '(\\s|$)'))
}

// Add class to element
export const addClass = (ele: HTMLElement, className: string) => {
  if (!hasClass(ele, className)) ele.className += ' ' + className
}

// Remove class from element
export const removeClass = (ele: HTMLElement, className: string) => {
  if (hasClass(ele, className)) {
    const reg = new RegExp('(\\s|^)' + className + '(\\s|$)')
    ele.className = ele.className.replace(reg, ' ')
  }
}

// Toggle class for the selected element
export const toggleClass = (ele: HTMLElement, className: string) => {
  if (!ele || !className) {
    return
  }
  let classString = ele.className
  const nameIndex = classString.indexOf(className)
  if (nameIndex === -1) {
    classString += '' + className
  } else {
    classString =
      classString.substr(0, nameIndex) +
      classString.substr(nameIndex + className.length)
  }
  ele.className = classString
}

export const addFloat = (a: number, b: number) => {
  const aStr = a.toString()
  const bStr = b.toString()
  const aDecimals = (aStr.split('.')[1] || '').length
  const bDecimals = (bStr.split('.')[1] || '').length
  const maxDecimals = Math.max(aDecimals, bDecimals)
  const factor = Math.pow(10, maxDecimals)

  return (Math.round(a * factor) + Math.round(b * factor)) / factor
}

export const deepClone = (obj: any): any => {
  if (obj === null || typeof obj !== 'object') return obj

  if (obj instanceof Date) return new Date(obj)
  if (obj instanceof RegExp) return new RegExp(obj)
  if (obj instanceof Array) {
    return obj.map(item => deepClone(item))
  }

  const cloned = {}
  for (const key in obj) {
    cloned[key] = deepClone(obj[key])
  }
  return cloned
}

export const toMaximumTimeOfDay = (date: Date): Date => {
  return new Date(date.setHours(23, 59, 59, 999))
}

export const toStringMaximumTimeOfDay = (date: Date): string => {
  const _date = new Date(date.setHours(23, 59, 59, 999))
  return dayjs(_date).format('YYYY-MM-DD HH:mm:ss')
}

export const formatDecimal = (val: string | number, decimalPlaces: number, isPositive?: boolean | null) => {
  if (val === null || val === undefined) return ''

  let value = String(val)

  // 根据正负限制过滤
  if (isPositive === true) {
    // 只允许正数
    value = value.replace(/[^\d.]/g, '')
  } else if (isPositive === false) {
    // 只允许负数
    value = value.replace(/[^\d.-]/g, '')
    // 负号只能存在开头
    value = value.replace(/(?!^)-/g, '')
    // 自动补负号
    if (value && !value.startsWith('-')) {
      value = '-' + value
    }
  } else {
    // 不限制正负
    value = value.replace(/[^\d.-]/g, '')
    // 负号只能存在开头
    value = value.replace(/(?!^)-/g, '')
  }

  // 没有小数
  if (decimalPlaces === 0) {
    value = value.replace(/[^\d-]/g, '')
    return value
  }
  // 只允许一个小数点
  value = value.replace(/\.{2,}/g, '.')
  value = value
    .replace('.', '#DOT#')
    .replace(/\./g, '')
    .replace('#DOT#', '.')
  // 处理整数部分前导0
  const negative = value.startsWith('-')
  let result = negative ? value.substring(1) : value
  if (result.startsWith('0') && result.length > 1 && result[1] !== '.') {
    result = result.replace(/^0+/, '')
  }
  // 限制小数位
  if (result.includes('.')) {
    const [integerPart, decimalPart = ''] = result.split('.')
    result = `${integerPart}.${decimalPart.slice(0, decimalPlaces)}`
    if (result.startsWith('.')) {
      result = '0' + result
    }
  }
  return negative ? '-' + result : result
}

export const formatThousands = (val: string | number | null | undefined): string => {
  if (val === null || val === undefined || val === '') {
    return ''
  }
  if (val === 0) return '0.00'
  let num: number
  if (typeof val === 'string') {
    // 去除字符串中的空格和原有逗号
    const cleanStr = val.trim().replace(/,/g, '')
    num = parseFloat(cleanStr)
  } else {
    num = val
  }
  // 检查是否为有效数字
  if (isNaN(num) || !isFinite(num)) {
    // 返回原字符串（如果不是数字字符串）
    return typeof val === 'string' ? val : ''
  }

  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
